import React from "react";
import {
  createGenerateClassName,
  createTheme,
  StylesProvider,
  ThemeProvider,
  Container,
  withStyles,
  Box,
  Typography,
  Grid,
  CardMedia
} from "@material-ui/core";
import scheduling from "../../assets/images/scheduling.png";
import lessonManagement from "../../assets/images/lesson-management.png";
import payment from "../../assets/images/payment.png";
import event from "../../assets/images/event.png";
import smsAndEmail from "../../assets/images/sms-and-email.png";
import skill from "../../assets/images/skill-tracking.png";
import googleIntegration from "../../assets/images/google-integration-1.png";
import reporting from "../../assets/images/reporting.png";

const generateClassName = createGenerateClassName({
  productionPrefix: "c",
  disableGlobal: true,
  seed: "Features",
});

const theme = createTheme({
  typography: {
    fontFamily: "'Varela Round', sans-serif",
  },
  overrides: {
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: "#333333",
        },
      },

      focused: {
        color: "#333333",
      },
    },
    MuiInput: {
      underline: {
        "&::after": {
          borderBottom: "2px solid #333333",
        },
      },
    },
  },
});

const FeaturesBox = withStyles(() => ({
  root: {
    border: "1px solid #E0E0E0",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)",
    borderRadius: 16,
    background: "#EDF6F8",
    padding: 36,
    marginBottom: 20,
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
      padding: "32px 16px",
      minHeight: "auto",
    }
  },
}))(Box);

const HeadLine = withStyles((theme) => ({
  root: {
    fontSize: 30,
    fontWeight: 600,
    lineHeight: "40px",
    marginBottom: 16,
    color: "#007BFF",
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
      lineHeight: "28px",
      fontWeight: 400,
    },
  },
}))(Typography);

const FeaturesContainer = withStyles(() => ({
  root: {
    marginTop: 20,
    marginBottom: 20,
  },
}))(Container);

const ImageBox = withStyles(() => ({
  root: {
    display: "flex",
    justifyContent: "center"
  },
}))(Box);

const CardMediaImage = withStyles((theme) => ({
  root: {
    width: 430,
    height: 278,
    [theme.breakpoints.down("sm")]: {
      height: 0,
      paddingTop: "50%",
      backgroundSize: "contain",
    },
  },
}))(CardMedia);

const Title = withStyles((theme) => ({
  root: {
    fontSize: 20,
    fontWeight: 400
  },
}))(Typography);

const Features = () => {

  return (
    <>
      <StylesProvider generateClassName={generateClassName}>
        <ThemeProvider theme={theme}>
          <FeaturesContainer>
            <FeaturesBox>
              <HeadLine>
                Scheduling
              </HeadLine>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Title> Studio Owner </Title>
                  <ul>
                    <li>
                      Manage staff schedules. <b>Book students</b> on behalf of instructors and vice versa.
                    </li>
                  </ul>

                  <Title> Instructor </Title>
                  <ul>
                    <li>
                      Propose to and accept lesson times from students.
                    </li>
                    <li>
                      Schedule 2x, 3x, and <b> recurring lessons. </b>
                    </li>
                  </ul>

                  <Title> Student </Title>
                  <ul>
                    <li>
                      Propose to and <b> accept lesson times </b> from instructors.
                    </li>
                    <li>
                      Schedule 2x, 3x, and recurring lessons.
                    </li>
                  </ul>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageBox>
                    <CardMediaImage image={scheduling} alt="" />
                  </ImageBox>
                </Grid>
              </Grid>
            </FeaturesBox>

            <FeaturesBox>
              <HeadLine>
                Lesson Management
              </HeadLine>
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <ImageBox>
                      <CardMediaImage image={lessonManagement} alt="" />
                    </ImageBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Title> Studio Owner </Title>
                    <ul>
                      <li>
                        Create <b>lesson packages.</b>
                      </li>
                      <li>
                        Track how many lessons remain via shared ledger (no more disputes!)
                      </li>
                    </ul>

                    <Title> Instructor </Title>
                    <ul>
                      <li>
                        <b>Coordinate schedules</b> across all your students.
                      </li>
                      <li>
                        Choose whether to share your availability.
                      </li>
                    </ul>

                    <Title> Student </Title>
                    <ul>
                      <li>
                        <b>Always know</b> how many lessons you have left in your package.
                      </li>
                      <li>
                        Review your past and upcoming lessons.
                      </li>
                    </ul>
                  </Grid>
                </Grid>
              </Box>
            </FeaturesBox>

            <FeaturesBox>
              <HeadLine>
                Payments
              </HeadLine>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Title> Studio Owner </Title>
                  <ul>
                    <li>
                      Accept online <b>credit card</b> payments.
                    </li>
                    <li>
                      Keep a log of in-person, cash, and Zelle transactions.
                    </li>
                  </ul>

                  <Title> Instructor </Title>
                  <ul>
                    <li>
                      Easier payments for students translates to <b>more lessons</b> booked.
                    </li>
                    <li>
                      Prepayment-only can be turned on.
                    </li>
                  </ul>

                  <Title> Student </Title>
                  <ul>
                    <li>
                      <b>Always know</b> how many lessons you have left in your package.
                    </li>
                    <li>
                      Pay with <b>one click</b> from your phone — no more need to pay in-person at the studio.
                    </li>
                  </ul>
                </Grid>
                <Grid item xs={12} sm={6}>
                <ImageBox>
                  <CardMediaImage image={payment} alt="" />
                </ImageBox>
                </Grid>
              </Grid>
            </FeaturesBox>

            <FeaturesBox>
              <HeadLine>
                Events and Merchandise
              </HeadLine>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                <ImageBox>
                  <CardMediaImage image={event} alt="" />
                </ImageBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Title> Studio Owner </Title>
                  <ul>
                    <li>
                      Post bulletins in the digital community space. Students can purchase tickets to events, as well as <b>any product or service</b> you offer.
                    </li>
                  </ul>

                  <Title> Instructor </Title>
                  <ul>
                    <li>
                      More avenues for <b> student engagement. </b>
                    </li>
                    <li>
                      You can sell items as well as services.
                    </li>
                  </ul>

                  <Title> Student </Title>
                  <ul>
                    <li>
                      One-click sign up for studio events. Purchase products and services through <b>ecommerce bulletins.</b>
                    </li>
                  </ul>
                </Grid>
              </Grid>
            </FeaturesBox>

            <FeaturesBox>
              <HeadLine>
                SMS and Email
              </HeadLine>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Title> Studio Owner </Title>
                  <ul>
                    <li>
                      <b>Communicate</b> with all your students, keeping them in the loop with what’s happening at the studio.
                    </li>
                  </ul>

                  <Title> Instructor </Title>
                  <ul>
                    <li>
                      <b>Automated</b> communication for lesson scheduling and other flows save you immense time and hassle.
                    </li>
                  </ul>

                  <Title> Student </Title>
                  <ul>
                    <li>
                      Receive lesson reminders. Stay in the loop with new studio announcements. Get alerted with <b> instant updates. </b>
                    </li>
                  </ul>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ImageBox>
                    <CardMediaImage image={smsAndEmail} alt="" />
                  </ImageBox>
                </Grid>
              </Grid>
            </FeaturesBox>

            <FeaturesBox>
              <HeadLine>
                Skill Tracking
              </HeadLine>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <ImageBox>
                    <CardMediaImage image={skill} alt="" />
                  </ImageBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Title> Studio Owner </Title>
                  <ul>
                    <li>
                      Improve your service offering by incorporating <b>lesson notes</b> and <b>video upload</b>. Review the notes of your staff.
                    </li>
                  </ul>

                  <Title> Instructor </Title>
                  <ul>
                    <li>
                      You can leave <b>lesson notes</b> as well as review such notes from your student. Review your students’ practice as well.
                    </li>
                  </ul>

                  <Title> Student </Title>
                  <ul>
                    <li>
                      Write down what you learned and <b>record videos</b>, so you can cement the knowledge and maximize your lesson value. Log your practice sessions.
                    </li>
                  </ul>
                </Grid>
              </Grid>
            </FeaturesBox>

            <FeaturesBox>
              <HeadLine>
                Google Integration
              </HeadLine>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Title> Studio Owner </Title>
                  <ul>
                    <li>
                      Single Sign On. Match instructor availability with student availability through <b>Google Calendar sync.</b>
                    </li>
                  </ul>

                  <Title> Instructor </Title>
                  <ul>
                    <li>
                      Single Sign On. <b>Unify</b> your Google and Swyvel calendars.
                    </li>
                    <li>
                      Proactively schedule with students based on their availability.
                    </li>
                  </ul>

                  <Title> Student </Title>
                  <ul>
                    <li>
                      Single Sign On. <b>Share your availability</b> at will with the instructor — let them do the work of scheduling you.
                    </li>
                  </ul>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageBox>
                    <CardMediaImage image={googleIntegration} alt="" />
                  </ImageBox>
                </Grid>
              </Grid>
            </FeaturesBox>

            <FeaturesBox>
              <HeadLine>
                Reporting
              </HeadLine>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <ImageBox>
                    <CardMediaImage image={reporting} alt="" />
                  </ImageBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Title> Studio Owner </Title>
                  <ul>
                    <li>
                      Track top-down and bottoms-up <b>performance</b> across your business. Monitor student engagement.
                    </li>
                  </ul>

                  <Title> Instructor </Title>
                  <ul>
                    <li>
                      Showcase your <b>effective management</b> of students on the instructor leaderboard.
                    </li>
                  </ul>

                  <Title> Student </Title>
                  <ul>
                    <li>
                      Track your <b>skills progression</b> and share practice sessions with your instructor.
                    </li>
                  </ul>
                </Grid>
              </Grid>
            </FeaturesBox>
          </FeaturesContainer>
        </ThemeProvider>
      </StylesProvider>
    </>
  );
};

export default Features;
